import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const isImagePath = (value) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(value || "");
const isPdfPath = (value) => /\.pdf$/i.test(value || "");

const fileLabel = (path) => {
  const name = String(path || "").split("/").pop() || "Document";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const resolveFileUrl = (path, cdnUrl) => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = String(cdnUrl || process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    ""
  );
  return `${base}/${value.replace(/^\//, "")}`;
};

const actorName = (user) => {
  if (!user) return "";
  if (typeof user === "string") return user.trim();
  return String(user.name || user.userName || "").trim();
};

const namedUser = (...users) => {
  for (const user of users) {
    const name = actorName(user);
    if (name) return name;
  }
  return "-";
};

const historyRecords = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.history)) return data.history;
  if (Array.isArray(data?.documents)) return data.documents;
  return data ? [data] : [];
};

const filePaths = (item) => {
  if (Array.isArray(item?.filesPath)) return item.filesPath.filter(Boolean);
  if (Array.isArray(item?.filePath)) return item.filePath.filter(Boolean);
  if (item?.filePath) return [item.filePath];
  if (item?.path) return [item.path];
  return [];
};

const toPreview = (path, fileName, cdnUrl) => {
  const url = resolveFileUrl(path, cdnUrl);
  return {
    name: fileName,
    label: fileLabel(path),
    url,
    path,
    isImage: isImagePath(path) || isImagePath(url),
    isPdf: isPdfPath(path) || isPdfPath(url),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const buildHistoryRows = (data, cdnUrl) =>
  historyRecords(data).map((record, recordIndex) => {
    const entries =
      Array.isArray(record?.file) && record.file.length ? record.file : [record];
    const files = entries.map((item, itemIndex) => {
      const fileName =
        String(item?.fileName || record?.fileName || record?.documentName || "")
          .trim()
          .toUpperCase() || "-";
      const paths = filePaths(item);
      return {
        key: `${record?._id || recordIndex}-${itemIndex}`,
        fileName,
        previews: paths.map((path) => toPreview(path, fileName, cdnUrl)),
      };
    });
    return {
      key: record?._id || recordIndex,
      files,
      createdBy: namedUser(record?.createdBy),
      updatedBy: namedUser(record?.updatedBy),
      createdAt: formatDateTime(record?.createdAt),
    };
  });

const FileThumb = ({ file, compact = false }) => {
  const [failed, setFailed] = useState(false);
  const height = compact ? 72 : 140;

  if (file.isImage && !failed) {
    return (
      <Box
        component="img"
        src={file.url}
        alt={file.name}
        onError={() => setFailed(true)}
        sx={{
          width: compact ? 72 : "100%",
          height,
          objectFit: "cover",
          display: "block",
          background: "#F4F5F7",
          borderRadius: compact ? "8px" : 0,
        }}
      />
    );
  }

  if (file.isPdf && !failed) {
    return (
      <Box
        sx={{
          width: compact ? 72 : "100%",
          height,
          overflow: "hidden",
          background: "#F4F5F7",
          borderRadius: compact ? "8px" : 0,
        }}
      >
        <iframe
          title={file.label}
          src={`${file.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          style={{
            width: "100%",
            height,
            border: "none",
            pointerEvents: "none",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height,
        width: compact ? 72 : "100%",
        borderRadius: compact ? "8px" : 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#F7F4FC",
        color: file.isPdf ? "#C62828" : "#752ADA",
      }}
    >
      {file.isPdf ? (
        <PictureAsPdfIcon sx={{ fontSize: 42 }} />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 42 }} />
      )}
    </Box>
  );
};

const LargePreview = ({ file, onClose }) => (
  <Dialog open={Boolean(file)} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        pr: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight={700} noWrap>
          {file?.name}
        </Typography>
        {file?.label && file.label !== file.name && (
          <Typography fontSize={12} color="#7B8499" noWrap>
            {file.label}
          </Typography>
        )}
      </Box>
      <IconButton onClick={onClose} aria-label="Close preview">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ textAlign: "center", pb: 3 }}>
      {file?.isImage ? (
        <Box
          component="img"
          src={file.url}
          alt={file.name}
          sx={{
            maxWidth: "100%",
            maxHeight: "75vh",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      ) : file?.isPdf ? (
        <iframe
          title={file.label || file.name}
          src={file.url}
          style={{
            width: "100%",
            height: "75vh",
            border: "none",
            borderRadius: 8,
          }}
        />
      ) : (
        <Typography color="#7B8499" py={6}>
          Preview is not available for this file.
        </Typography>
      )}
    </DialogContent>
  </Dialog>
);

const JewellerDocumentsDialog = ({ open, jeweller, onClose }) => {
  const cdnUrl = useSelector((state) => state?.appConfig?.configData?.cdnUrl);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const jewellerId = jeweller?._id || jeweller?.fullData?._id || "";
  const jewellerName =
    jeweller?.jewellerName || jeweller?.fullData?.jewellerName || "Jeweller";

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setRows([]);
      return undefined;
    }

    if (!jewellerId) {
      toast.error("Jeweller id is required");
      return undefined;
    }

    let cancelled = false;
    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/getDocumentHistory`,
          { params: { jewellerId } }
        );
        if (cancelled) return;
        if (response?.data?.success === false) {
          toast.error(response?.data?.message || "Failed to fetch document history");
          setRows([]);
          return;
        }
        setRows(buildHistoryRows(response?.data?.data, cdnUrl));
      } catch (error) {
        if (cancelled) return;
        toast.error(
          error.response?.data?.message || "Failed to fetch document history"
        );
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [open, jewellerId, cdnUrl]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            position: "relative",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            color: "#fff",
          }}
        >
          Documents — {jewellerName}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[300],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 220, background: "#FAFAFC" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#752ADA" }} />
            </Box>
          )}

          {!loading && rows.length === 0 && (
            <Typography color="#7B8499" textAlign="center" py={6}>
              No documents uploaded for this jeweller.
            </Typography>
          )}

          {!loading && rows.length > 0 && (
            <TableContainer
              sx={{
                border: "1px solid #E6E8EC",
                borderRadius: "12px",
                background: "#fff",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#F7F4FC" }}>
                    {["File Name", "File", "Created By", "Updated By", "Created At"].map(
                      (heading) => (
                        <TableCell
                          key={heading}
                          sx={{ fontWeight: 700, color: "#202124" }}
                        >
                          {heading}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {row.files.map((entry) => (
                            <Box
                              key={entry.key}
                              sx={{ minHeight: 72, display: "flex", alignItems: "center" }}
                            >
                              {entry.fileName}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {row.files.map((entry) => (
                            <Box
                              key={entry.key}
                              sx={{
                                minHeight: 72,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              {entry.previews.length ? (
                                entry.previews.map((file) => (
                                  <Box
                                    key={file.url}
                                    onClick={() => setPreview(file)}
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                      cursor: "pointer",
                                      maxWidth: 280,
                                    }}
                                  >
                                    <FileThumb file={file} compact />
                                    <Typography
                                      noWrap
                                      fontSize={12}
                                      color="#5f6368"
                                      title={file.label}
                                    >
                                      {file.label}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                "-"
                              )}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>{row.createdBy}</TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>{row.updatedBy}</TableCell>
                      <TableCell sx={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                        {row.createdAt}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <LargePreview file={preview} onClose={() => setPreview(null)} />
    </>
  );
};

export default JewellerDocumentsDialog;
